import Head from 'next/head'
import classes from '@src/styles/HowWeWork.module.css';
import BusinessIcon from '@src/assets/business-icon-blue.svg'
import PersonIcon from '@src/assets/icons/user-blue.svg'
import themeClasses from "@src/styles/theme.module.css";
import { segmentTrack } from '@src/functions/segment';
import { useState, useEffect } from 'react';
import submitSubmissionRequest from '@src/functions/request-subscription';
import SubscriptionRequestModal from './SubscriptionRequestModal';

const formDefaults = {
  name: "",
  companyName: "",
  email: "",
  phone: "",
  description: "",
};

function Subscriptions() {
  const [fields, setFields] = useState(formDefaults);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [message, setMessage] = useState<null | "success" | "error">(null);

  useEffect(() => {
    segmentTrack('Subscription Page Loaded');
  }, []);

  const content = {
    title: "Junk Removal and Dumpster Subscriptions - Monthly | Dimension Waste Management",
    description: "Looking for a steady solution? Try our Residential, Small Business or Commercial waste management options.",
  };

  const options = [
    {
      id: "residential",
      title: "Residential",
      description: "Ideal for households needing to dispose of bulky items, this plan offers the ultimate convenience. Enjoy unlimited pickups per month with up to 2 large items each time. Reclaim your space without breaking a sweat!",
      perks: [
        "Unlimited pickups",
        "Same-day service",
        "Easy cancellation",
        "No hidden fees",
      ],
      price: 9900,
      icon: PersonIcon.src,
    },
    {
      id: "small-business",
      title: "Small Business",
      description: "Tailored for small businesses with larger waste removal needs, this plan keeps your workspace organized. With unlimited pickups per month and up to 5 large items each time, focus on your business while we take care of the clutter.",
      perks: [
        "Unlimited pickups",
        "Same-day service",
        "Easy cancellation",
        "No hidden fees",
      ],
      price: 19900,
      icon: BusinessIcon.src,
    },
    {
      id: "commercial",
      title: "Commercial",
      description: "Designed for facilities with extensive bulk item disposal needs, this plan streamlines your waste management process. Benefit from unlimited pickups and up to a truckload of large items each time, ensuring an efficient workspace.",
      perks: [
        "Unlimited pickups",
        "Same-day service",
        "Easy cancellation",
        "No hidden fees",
      ],
      price: 39900,
      icon: BusinessIcon.src,
    },
  ]


  const clearFields = () => setFields(formDefaults);
  const handleClose = () => {
    setSubmitting(false);
    setSelected(null);
  }

  const handleOptionClick = (subscription: any) => {
    setSelected(subscription);
    segmentTrack('Subscription Option Clicked', {
      ...subscription,
    });
  };

  const isValidEmail = (email: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmitForm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);
    segmentTrack('Subscription Request Submitted', {
      ...fields,
    });
    if (!fields.name || !fields.email || !isValidEmail(fields.email) || !selected) {
      // eslint-disable-next-line no-alert
      setMessage("error");
      setSubmitting(false);
      return;
    }
    const response = await submitSubmissionRequest(fields, {
      id: selected?.id,
      title: selected?.title,
      price: selected?.price,
    });
    if (response?.success) {
      setMessage("success");
      clearFields();
    } else {
      setMessage("error");
    }
    setSubmitting(false);
  };

  const handleChangeFields = (event: any) => {
    setFields((state: any) => ({
      ...state,
      [event.target.name]: event.target.value,
    }));
  }


  // `Contact Entered` is a funnel signal: it marks that the visitor started
  // filling in contact details, and which fields they have completed so far. It
  // deliberately carries no name, email, phone or company name — this fires on
  // every field blur, so sending those would scatter identifying data across
  // analytics for a form the visitor may never submit. The real values still go
  // to the backend on submit, which is the only place they are needed.
  const handleBlur = (e: any) => {
    if (e.target.value) {
      segmentTrack("Contact Entered", {
        field: e.target.name,
        hasName: Boolean(fields.name),
        hasEmail: Boolean(fields.email),
        hasCompanyName: Boolean(fields.companyName),
        hasPhone: Boolean(fields.phone),
        from: "Subscription Form",
        site: "NextJS",
      })
    }
  }

  const subText = `
  Simplify your life with our easy and reliable bulk item removal plans. 
  Perfect for disposing of large junk items from bins to pallets through regular pickups or dumpster dropoffs. 
  Choose the plan that suits your needs and let us handle the heavy lifting!
  `;

  return (
    <>
      <Head>
        <title>
          {content.title}
        </title>
        <meta name="description" content={content.description} />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      <section>
        <div style={{ textAlign: 'center', padding: '40px 0' }} className={themeClasses.lightBackground}>
          <div className={classes.textContainer}>
            <h2 style={{ color: '#fff', fontSize: '36px', fontWeight: 'bold', marginBottom: '16px' }}>
              Choose Your Bulk Item Removal Plan
            </h2>
            <h5 style={{ color: '#fff', fontSize: '18px', maxWidth: '800px', margin: '0 auto', fontWeight: "400" }}>{subText}</h5>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '8px', paddingBottom: '32px' }} className={themeClasses.lightBackground}>
        <div style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', maxWidth: '1200px', marginTop: -50 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', columnGap: '16px', rowGap: '16px', padding: 12}}>
            {options.map((option) => (
              <div key={option.title}>
                <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '32px', backgroundColor: '#fff' }}>
                  <img style={{ display: 'block', margin: '0 auto', paddingBottom: '8px', color: "blue" }} alt='' src={option.icon} />
                  <h2 style={{ textAlign: 'center', fontSize: '24px', color: '#222', marginBottom: '8px' }}>{option.title}</h2>
                  <h3 style={{ textAlign: 'center', fontSize: '16px', color: '#444', marginBottom: -32, opacity: 0.7, fontWeight: 400 }}>starting at</h3>
                  <h3 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 'bold', color: '#444', marginBottom: '0px' }}>${option.price / 100}</h3>
                  <h3 style={{ textAlign: 'center', fontSize: '16px', color: '#444', marginBottom: '16px', marginTop: -4, fontWeight: 400 }}>per month</h3>
                  <p style={{ color: '#666', marginBottom: '16px' }}>{option.description}</p>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '16px' }}>
                    {option.perks.map((perk) => (
                      <li key={perk} style={{ color: '#666' }}>{perk}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleOptionClick(option)}
                    className={themeClasses.primaryButton}
                    style={{ display: 'block', margin: '0 auto', borderRadius: '4px', cursor: 'pointer', fontSize: '16px', padding: '8px 16px' }}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <h6 style={{ fontSize: '18px', color: '#fff', fontWeight: 400 }}>Not ready for a recurring plan? Experience our top-notch service with a one-time bulk item pickup. <a href="https://app.getdimension.com/booking" style={{ color: '#1a73e8', textDecoration: 'underline' }}>Book now</a></h6>
        </div>
      </section>
      <SubscriptionRequestModal open={!!selected} option={selected} values={fields} onClose={handleClose} onChange={handleChangeFields} onSubmit={handleSubmitForm} message={message} submitting={submitting} handleBlur={handleBlur} />
    </>
  )
}

export default Subscriptions;